import { useMutation, useQueryClient } from '@tanstack/react-query';
import * as Effect from 'effect/Effect';
import { pipe } from 'effect/Function';
import { ApiClient } from '@/api/client';
import { MainLayer } from '@/effects/layers';
import { runEffect } from '@/effects/runtime';
import { QUERY_KEYS } from '@/lib/constants';
import {
  PoolInsertRequestSchema,
  PoolInsertResponseSchema,
  type PoolInsertRequest
} from '@/api/schemas/funding-pool.schema';

interface CreatePoolInsertInput extends PoolInsertRequest {
  poolId?: string;
}

export function useCreatePoolInsert(groupId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: CreatePoolInsertInput) => {
      const payload = PoolInsertRequestSchema.parse({
        amount: input.amount,
        currency_code: input.currency_code,
        description: input.description,
        inserted_by: input.inserted_by,
        date: input.date,
      });

      const program = pipe(
        Effect.gen(function* () {
          const apiClient = yield* ApiClient;
          const url = input.poolId
            ? `/groups/${groupId}/pools/${input.poolId}/insert`
            : `/groups/${groupId}/pools/insert`;
          const response = yield* apiClient.post(
            url,
            payload,
            PoolInsertResponseSchema
          );
          return response;
        }),
        Effect.provide(MainLayer)
      );

      return runEffect(program);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.TRANSACTIONS, groupId] });
      await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GROUP_BALANCES, groupId] });
      await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.FUNDING_POOLS, groupId] });
    },
  });
}
